import React, { useEffect, useRef, useState } from "react";
import { LockKey, LockKeyOpen, ShieldCheck, UserGear, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAdminMode, MODE_AGENT, MODE_FULL, MODE_PRESIDENT } from "../../context/AdminModeContext";

function formatExpiry(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  } catch { return ""; }
}

function PasscodePrompt({ onSubmit, onClose }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const submit = (e) => {
    e.preventDefault();
    const ok = onSubmit(code);
    if (!ok) {
      setError("Wrong passcode");
      setCode("");
      ref.current?.focus();
    }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="mode-passcode-prompt">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-sm rounded-xl border border-ink-600 bg-ink-900 p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold tracking-[0.25em] uppercase text-zinc-300">Enter passcode</h3>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-neon-magenta" aria-label="Close" data-testid="mode-passcode-close">
            <X size={18} weight="bold" />
          </button>
        </div>
        <p className="text-sm text-zinc-400 mb-4">Unlock <span className="text-neon-cyan font-semibold">Full mode</span>.</p>
        <input
          ref={ref}
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={code}
          onChange={(e) => { setCode(e.target.value); setError(""); }}
          placeholder="• • •"
          className="w-full px-4 py-3 rounded-md bg-ink-800 border border-ink-600 text-white text-center tracking-[0.5em] text-xl font-mono focus:outline-none focus:border-neon-cyan/60"
          data-testid="mode-passcode-input"
        />
        {error && <p className="text-xs text-neon-magenta mt-2 text-center" data-testid="mode-passcode-error">{error}</p>}
        <button type="submit" className="mt-4 w-full px-4 py-2.5 rounded-md bg-neon-cyan text-black text-sm font-bold tracking-wide hover:bg-neon-cyanHover" data-testid="mode-passcode-submit">
          Unlock
        </button>
      </form>
    </div>
  );
}

function ModeBadge({ isFull }) {
  // President mode intentionally renders as "Full" to keep it secret.
  if (isFull) return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.25em] uppercase text-neon-cyan">
      <ShieldCheck size={11} weight="fill" /> Full
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-400">
      <UserGear size={11} weight="bold" /> Agent
    </span>
  );
}

export default function ModeSelector() {
  const { mode, expiresAt, unlock, lock, isFull } = useAdminMode();
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(false);

  const onPasscodeSubmit = (code) => {
    const unlocked = unlock(code);
    if (!unlocked) return false;
    // Toast message is intentionally the same for both 111 and 222 to keep
    // the owner-mode entrance secret. The owner will recognize it via the
    // upper-left gold star indicator that appears for president mode only.
    if (unlocked === MODE_PRESIDENT) toast.success("Full mode unlocked");
    else if (unlocked === MODE_FULL) toast.success("Full mode unlocked — expires next Thursday");
    setPrompt(false);
    setOpen(false);
    return true;
  };

  const onLock = () => {
    lock();
    toast.success("Back to Agent mode");
    setOpen(false);
  };

  return (
    <>
      <div className="fixed bottom-28 right-5 z-50 print:hidden" data-testid="mode-selector">
        {open && (
          <div className="mb-3 rounded-xl border border-ink-600 bg-ink-900/95 backdrop-blur-md shadow-2xl p-2 w-60" data-testid="mode-selector-menu">
            <div className="px-3 py-2 border-b border-ink-700 mb-1 flex items-center justify-between">
              <ModeBadge isFull={isFull} />
              {mode === MODE_FULL && expiresAt && (
                <span className="text-[9px] font-mono text-zinc-500" title="Auto-locks at this time">
                  until {formatExpiry(expiresAt)}
                </span>
              )}
            </div>
            <button
              onClick={onLock}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded text-xs text-left transition-colors ${mode === MODE_AGENT ? "bg-ink-800 text-white font-semibold" : "text-zinc-300 hover:bg-ink-800"}`}
              data-testid="mode-select-agent"
            >
              <UserGear size={14} weight="bold" />
              <span className="flex-1">Agent mode</span>
              {mode === MODE_AGENT && <span className="text-[9px] text-neon-cyan font-mono">active</span>}
            </button>
            <button
              onClick={() => setPrompt(true)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded text-xs text-left transition-colors ${isFull ? "bg-neon-cyan/10 text-neon-cyan font-semibold" : "text-zinc-300 hover:bg-ink-800"}`}
              data-testid="mode-select-full"
              title={isFull ? "Enter another passcode to re-key or elevate" : "Enter passcode 111 to unlock"}
            >
              <ShieldCheck size={14} weight="fill" />
              <span className="flex-1">{isFull ? "Re-enter passcode" : "Full mode"}</span>
              {isFull ? (
                <span className="text-[9px] text-neon-cyan font-mono">active</span>
              ) : (
                <LockKey size={11} className="text-zinc-500" />
              )}
            </button>
          </div>
        )}

        <button
          onClick={() => setOpen((v) => !v)}
          className={`flex items-center gap-2 px-3.5 py-2.5 rounded-full border shadow-lg transition-all ${
            isFull
              ? "border-neon-cyan/40 bg-neon-cyan/10 text-neon-cyan hover:bg-neon-cyan/15"
              : "border-ink-600 bg-ink-900/90 text-zinc-300 hover:border-neon-cyan/40 hover:text-neon-cyan"
          }`}
          data-testid="mode-selector-toggle"
          aria-label="Switch mode"
        >
          {isFull ? <LockKeyOpen size={16} weight="fill" /> : <LockKey size={16} weight="bold" />}
          <ModeBadge isFull={isFull} />
        </button>
      </div>

      {prompt && <PasscodePrompt onSubmit={onPasscodeSubmit} onClose={() => setPrompt(false)} />}
    </>
  );
}
